import { LayerCard, Tabs, Text } from '@cloudflare/kumo'
import { useState, type ReactNode } from 'react'
import { useTranslation } from '../../i18n'
import { detectPlatform, type WifiPlatform } from './detectPlatform'
import './wifi.css'

const PLATFORMS: WifiPlatform[] = ['iphone', 'android', 'mac', 'windows']
const MASK = '•'.repeat(8)

function Fields({ rows }: { rows: [string, string][] }) {
  return (
    <ul className="flex flex-col gap-1 bg-kumo-recessed rounded-lg p-3 font-mono text-xs mt-2">
      {rows.map(([label, value]) => (
        <li key={label} style={{ overflowWrap: 'anywhere' }}>
          {label}: <strong>{value}</strong>
        </li>
      ))}
    </ul>
  )
}

export function SetupSteps({
  networkName,
  username,
  password,
  revealed,
}: {
  networkName: string
  username: string
  password: string
  revealed: boolean
}) {
  const { t } = useTranslation()
  const [detected] = useState<WifiPlatform>(() => detectPlatform())
  const [selected, setSelected] = useState<WifiPlatform>(detected)
  const shownPassword = `'${revealed ? password : MASK}'`
  const quotedUser = `'${username}'`
  const p = { networkName }

  const steps: Record<WifiPlatform, ReactNode[]> = {
    iphone: [t('wifi.setup.selectNetworkAndCredentials', p), t('wifi.setup.ios.clickJoinAllSet')],
    android: [
      t('wifi.setup.android.selectNetworkOnly', p),
      <>
        {t('wifi.setup.android.adjustSettings')}
        <Fields
          rows={[
            [t('wifi.setup.android.securityLabel'), 'EAP'],
            [t('wifi.setup.android.eapMethodLabel'), 'PEAP'],
            [t('wifi.setup.android.eapPhase2Label'), 'Automatic or MSCHAPV2'],
            [t('wifi.setup.android.serverCaLabel'), 'Default or Use system certificates'],
            [t('wifi.setup.android.macAddressTypeLabel'), 'Phone MAC'],
            [t('wifi.setup.android.domainLabel'), 'wenw.net'],
            [t('wifi.setup.android.identityLabel'), quotedUser],
            [t('wifi.password'), shownPassword],
            [t('wifi.setup.android.anonymousIdentityLabel'), t('wifi.setup.android.leaveBlank')],
          ]}
        />
      </>,
      t('wifi.setup.android.selectConnect'),
      t('wifi.setup.android.certificatePrompts'),
      t('wifi.setup.allSet'),
    ],
    mac: [
      <>
        {t('wifi.setup.macos.selectNetwork', p)}
        <Fields
          rows={[
            [t('wifi.setup.macos.modeLabel'), 'Automatic'],
            [t('wifi.username'), quotedUser],
            [t('wifi.password'), shownPassword],
          ]}
        />
      </>,
      t('wifi.setup.macos.clickJoin'),
      t('wifi.setup.macos.trustCertificate', p),
      t('wifi.setup.allSet'),
    ],
    windows: [
      t('wifi.setup.selectNetworkAndCredentials', p),
      t('wifi.setup.windows.clickOk'),
      t('wifi.setup.windows.trustCertificate', p),
      t('wifi.setup.allSet'),
    ],
  }

  return (
    <section className="flex flex-col gap-3">
      <Text as="h2" variant="heading2">
        {t('wifi.setup.title')}
      </Text>
      <Tabs
        variant="segmented"
        value={selected}
        onValueChange={(v) => setSelected(v as WifiPlatform)}
        tabs={PLATFORMS.map((value) => ({
          value,
          label: t(`wifi.setup.platform.${value}`),
          className: 'wifi-tabs__tab',
        }))}
        className="w-full"
        listClassName="w-full wifi-tabs__list"
      />
      {selected === detected && <Text variant="secondary">{t('wifi.setup.detectedHint')}</Text>}
      <LayerCard>
        <LayerCard.Primary>
          <ol
            className="flex flex-col gap-3 text-sm"
            style={{ listStyle: 'decimal', paddingInlineStart: '1.25rem' }}
          >
            {steps[selected].map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
          {(selected === 'mac' || selected === 'windows') && (
            <div style={{ paddingTop: 12 }}>
              <Text variant="secondary">{t('wifi.setup.forgetNetworkNote')}</Text>
            </div>
          )}
        </LayerCard.Primary>
      </LayerCard>
    </section>
  )
}
